// Cells — the single abstract usage metric for "how much memory does this user
// hold". A "cell" is one stored unit of memory, counted identically across
// project memory and personal memory:
//
//   cells = project_nodes + project_dimensions + current_self_facts
//
// "facts" is the SAME total split by origin — a separate, finer read:
//   • personalFacts = current self_facts (valid_to IS NULL)
//   • projectFacts  = nodes + dimensions (the entities that compose Context Maps)
//
// cells is the headline scalar that drives the organism's age (ageFromMemory)
// and is surfaced per-user on /admin. Artifacts (documents) and commits
// (history/events) are deliberately NOT cells.
//
// THIS IS THE TS SOURCE OF TRUTH FOR THE FORMULA. The admin RPC
// (supabase-migrations/036_admin_user_cells.sql) mirrors it in SQL — keep
// NODE_WEIGHT / DIMENSION_WEIGHT identical in both, or the dashboard's organism
// age and the admin roster's cell counts will silently disagree.

export const NODE_WEIGHT = 1;
export const DIMENSION_WEIGHT = 1; // a dimension is a coarser but real memory unit; equal weight keeps the number explainable

export interface CellBreakdown {
  /** the headline usage scalar — what drives the organism's age */
  cells: number;
  /** current personal facts (self_facts, valid_to IS NULL) */
  personalFacts: number;
  /** project memory units = nodes + dimensions (weighted) */
  projectFacts: number;
  nodes: number;
  dimensions: number;
  selfFacts: number;
}

/** Compute the cell breakdown from raw per-user counts. Pure — the one place the
 *  formula lives on the TS side. */
export function computeCells(i: { nodes: number; dimensions: number; selfFactsCurrent: number }): CellBreakdown {
  const nodes = Math.max(0, Math.trunc(i.nodes) || 0);
  const dimensions = Math.max(0, Math.trunc(i.dimensions) || 0);
  const selfFacts = Math.max(0, Math.trunc(i.selfFactsCurrent) || 0);
  const projectFacts = nodes * NODE_WEIGHT + dimensions * DIMENSION_WEIGHT;
  return {
    cells: projectFacts + selfFacts,
    personalFacts: selfFacts,
    projectFacts,
    nodes,
    dimensions,
    selfFacts,
  };
}
